/**
 * What the Publisher shows to an account that has not bought the Publisher plan.
 *
 * Presentation only, exactly like StudioLockedState next door: it takes a callback and
 * renders. The decision about whether to render it belongs to the route gate in
 * src/router.tsx, which is the one place that reads the entitlement, and the purchase
 * belongs to the screen the callback navigates to.
 *
 * §5 requires the upgrade screen AND that a non-entitled user cannot submit a release.
 * This component is only the first of those. The second is enforced independently in
 * api/publisherApi.submitRelease, which refuses with `no-entitlement` regardless of what
 * any screen decided — so hiding this screen would not open a path to submission.
 *
 * NOT A SECURITY BOUNDARY — the entitlement it reflects is a client-side boolean and
 * editable from devtools. See the header of src/stores/entitlementsStore.ts.
 */
import { Radio } from 'lucide-react';
import { Btn, SectionHeading, UpgradePrompt } from '../ui';
import { formatUpgradeOfferPriceLine, upgradeOffer } from '../../data/upgradeOffers';
import { c } from '../../styles/theme';

/**
 * The redesigned Publisher offer, shared with /products so locked pricing cannot drift.
 */
const PUBLISHER_OFFER = upgradeOffer('publisher-monetization');

export interface PublisherLockedStateProps {
  /** Sends the visitor to the screen that sells the Publisher plan. */
  onGetPublisher: () => void;
}

export default function PublisherLockedState({ onGetPublisher }: PublisherLockedStateProps) {
  return (
    <div>
      <SectionHeading
        as="h1"
        icon={<Radio size={17} color={c.accent} />}
        title="Lyrixsa AI PUBLISHER"
        subtitle="Take your music from Lyrixsa AI to the world’s leading music platforms."
        marginBottom={18}
      />
      <UpgradePrompt
        paddingY={28}
        icon={<Radio size={28} color={c.text2} />}
        title="Publishing requires the Publisher plan"
        description={PUBLISHER_OFFER?.product.desc ?? 'Distribute your music to streaming platforms.'}
        action={
          <Btn onClick={onGetPublisher}>
            <Radio size={13} />
            Get {PUBLISHER_OFFER?.product.name} — {formatUpgradeOfferPriceLine(PUBLISHER_OFFER?.product ?? { price: 197 })}
          </Btn>
        }
      />

      {/*
        The included publishing allowance is a product entitlement. Per-action usage stays
        out of the workspace and top-ups live inside the Publisher profile.
      */}
      <p
        style={{
          fontSize: 11.5,
          color: c.text2,
          lineHeight: 1.6,
          margin: '12px 0 0',
          textAlign: 'center',
        }}
      >
        The one-time plan includes{' '}
        <span style={{ fontFamily: 'IBM Plex Mono', color: c.text1 }}>10 Publisher Credits</span>.
      </p>
    </div>
  );
}
